import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, User, LogIn, ChevronDown } from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SiteSearch from "@/components/SiteSearch";
import ThemeToggle from "@/components/ThemeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage, langPrefix } from "@/i18n/LanguageContext";
import { useUserAuth } from "@/user/UserAuthContext";

/**
 * Public header.
 *
 * Navigation model — refined for launch:
 *   - Home is intentionally NOT in the nav; the logo on the start edge is the
 *     canonical way back to the homepage (standard convention + keeps the nav
 *     lean).
 *   - Five primary, equally-weighted items are visible on desktop:
 *       About · Tools · Workshops · Articles · Contact
 *   - Secondary destinations (Impact, Team) collapse into a single "More"
 *     dropdown so the primary row can breathe.
 *   - Every nav label uses `whitespace-nowrap` so the old "AI Tools" awkward
 *     line-wrap can never return. In English the label is simply "Tools" —
 *     the brand itself ("Syrian AI Society") already frames the whole site
 *     and matches the Arabic label (الأدوات) exactly.
 *
 * On mobile the drawer shows ALL seven destinations as a flat list (no "More"
 * grouping needed — the drawer has room).
 */

const NAV = {
  ar: {
    about: "عن المبادرة",
    tools: "الأدوات",
    workshops: "الورشات",
    articles: "المقالات",
    contact: "تواصل",
    impact: "الأثر",
    team: "الفريق",
    home: "الرئيسية",
    more: "المزيد",
    brand: "مجتمع الذكاء الاصطناعي السوري",
    logoAlt: "شعار مجتمع الذكاء الاصطناعي السوري",
    signInCta: "تسجيل الدخول",
    menuOpen: "فتح القائمة",
    menuClose: "إغلاق القائمة",
    primaryNav: "التنقل الرئيسي",
    mobileNav: "القائمة الرئيسية للجوال",
    signIn: "تسجيل الدخول",
    account: "حسابي",
  },
  en: {
    about: "About",
    tools: "Tools", // Intentionally simpler than "AI Tools" — avoids awkward wrap.
    workshops: "Workshops",
    articles: "Articles",
    contact: "Contact",
    impact: "Impact",
    team: "Team",
    home: "Home",
    more: "More",
    brand: "Syrian AI Society",
    logoAlt: "Syrian AI Society logo",
    signInCta: "Sign in",
    menuOpen: "Open menu",
    menuClose: "Close menu",
    primaryNav: "Primary navigation",
    mobileNav: "Mobile primary menu",
    signIn: "Sign in",
    account: "Account",
  },
};

export default function Navbar() {
  const { lang } = useLanguage();
  const t = NAV[lang];
  // Empty for English (canonical root), `/ar` for Arabic.
  const base = langPrefix(lang);
  const home = base || "/";
  const { user, status } = useUserAuth();

  // Primary row on desktop (five items — intentionally curated).
  const primaryLinks = [
    { to: `${base}/about`, label: t.about },
    { to: `${base}/tools`, label: t.tools },
    { to: `${base}/workshops`, label: t.workshops },
    { to: `${base}/articles`, label: t.articles },
    { to: `${base}/contact`, label: t.contact },
  ];

  // Secondary destinations that collapse into the "More" dropdown on desktop.
  const moreLinks = [
    { to: `${base}/impact`, label: t.impact },
    { to: `${base}/team`, label: t.team },
  ];

  // Flat mobile list (home appears first because the logo isn't always
  // obviously tappable on touch devices).
  const mobileLinks = [
    { to: home, label: t.home, end: true },
    ...primaryLinks,
    ...moreLinks,
  ];

  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Close mobile drawer on route change.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Close mobile drawer on Escape so keyboard users can dismiss it
  // exactly the way they dismiss the search panel and other modals.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // The "More" dropdown should look active when the current route matches one
  // of its grouped destinations.
  const isInMore = moreLinks.some((l) => location.pathname.startsWith(l.to));

  return (
    <>
    <header
      className={`sticky top-0 z-50 bg-white/90 backdrop-blur-[6px] transition-all ${
        scrolled
          ? "border-b border-[color:var(--sas-line)]"
          : "border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="sas-container">
        <div className="flex items-center justify-between h-[68px] gap-6">
          <LocalizedLink
            to="/"
            className="flex items-center shrink-0"
            data-testid="nav-logo"
            aria-label={t.brand}
            title={t.brand}
          >
            <img src="/sas-logo.svg" alt={t.logoAlt} className="h-9 w-9" />
          </LocalizedLink>

          {/* Primary nav — visible from lg up. Never wraps (whitespace-nowrap). */}
          <nav
            className="hidden lg:flex items-center gap-7 xl:gap-9"
            aria-label={t.primaryNav}
          >
            {primaryLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `sas-nav-link whitespace-nowrap ${isActive ? "active" : ""}`
                }
                data-testid={`nav-link-${l.to}`}
              >
                {l.label}
              </NavLink>
            ))}

            <DropdownMenu>
              <DropdownMenuTrigger
                className={`sas-nav-link whitespace-nowrap inline-flex items-center gap-1 outline-none focus-visible:text-[color:var(--sas-accent)] ${
                  isInMore ? "active" : ""
                }`}
                data-testid="nav-more-trigger"
              >
                {t.more}
                <ChevronDown size={14} aria-hidden className="opacity-70" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align={lang === "ar" ? "end" : "start"}
                sideOffset={10}
                className="min-w-[180px] rounded-xl border-[color:var(--sas-line)] bg-white p-1.5 shadow-[0_8px_28px_-12px_rgba(15,15,15,0.18)]"
              >
                {moreLinks.map((l) => (
                  <DropdownMenuItem key={l.to} asChild>
                    <NavLink
                      to={l.to}
                      className={({ isActive }) =>
                        `block rounded-lg px-3 py-2 text-[13.5px] cursor-pointer transition-colors ${
                          isActive
                            ? "text-[color:var(--sas-ink)] bg-[color:var(--sas-accent-soft)]/70"
                            : "text-[color:var(--sas-ink-2)] hover:text-[color:var(--sas-ink)] hover:bg-[color:var(--sas-accent-soft)]/50"
                        }`
                      }
                      data-testid={`nav-more-item-${l.to}`}
                    >
                      {l.label}
                    </NavLink>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </nav>

          {/* Desktop utility cluster: search · theme · language · sign-in */}
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            {/* xl+ gets the inline search bar; lg (1024–1279) uses the icon
                trigger so the nav row always stays on a single line. */}
            <div className="hidden xl:flex">
              <SiteSearch triggerVariant="bar" testId="site-search" />
            </div>
            <div className="flex xl:hidden">
              <SiteSearch testId="site-search-compact" />
            </div>
            <ThemeToggle lang={lang} testId="theme-toggle-header" />
            <LanguageSwitcher
              variant="globe"
              testId="lang-switcher-header"
            />
            <AuthWidget
              status={status}
              user={user}
              lang={lang}
              labels={t}
              variant="desktop-cta"
            />
          </div>

          {/* Mobile utility cluster — redesigned for balance:
                [logo] [expanded search bar] [globe] [auth icon] [hamburger]
              The search bar is `flex-1`, so it fills the leftover header
              width. The three icons after it are 40×40 glass pills so they
              feel like a matched set with the language globe. */}
          <div className="flex lg:hidden items-center gap-2 flex-1 min-w-0">
            <SiteSearch
              triggerVariant="mobile-bar"
              testId="site-search-mobile"
            />
            <LanguageSwitcher
              variant="globe"
              testId="lang-switcher-mobile-header"
            />
            {status !== "loading" && (
              user ? (
                <LocalizedLink
                  to="/account"
                  aria-label={t.account}
                  title={t.account}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full sas-glass shrink-0 text-[color:var(--sas-ink)] hover:border-[color:var(--sas-ink)] transition-colors"
                  data-testid="nav-account-mobile"
                >
                  {user.picture ? (
                    <img
                      src={user.picture}
                      alt=""
                      aria-hidden
                      className="w-7 h-7 rounded-full object-cover"
                    />
                  ) : (
                    <span
                      className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[12px] font-semibold"
                      style={{
                        background: "var(--sas-accent-soft)",
                        color: "var(--sas-accent)",
                      }}
                      aria-hidden
                    >
                      {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                </LocalizedLink>
              ) : (
                <LocalizedLink
                  to="/auth/login"
                  aria-label={t.signInCta}
                  title={t.signInCta}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full sas-glass shrink-0 text-[color:var(--sas-ink)] hover:border-[color:var(--sas-ink)] transition-colors"
                  data-testid="nav-signin-mobile"
                >
                  <LogIn size={17} aria-hidden />
                </LocalizedLink>
              )
            )}
            <button
              className="inline-flex items-center justify-center w-10 h-10 rounded-full sas-glass shrink-0 text-[color:var(--sas-ink)] hover:border-[color:var(--sas-ink)] transition-colors"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? t.menuClose : t.menuOpen}
              aria-expanded={open}
              aria-controls="mobile-drawer"
              data-testid="nav-toggle"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>
    </header>

      {/* ----------------------------------------------------------------- */}
      {/*  Mobile slide-in side drawer.                                     */}
      {/*                                                                   */}
      {/*  - Slides in from the natural "start" side of the layout:         */}
      {/*      LTR (English) → from the LEFT                                */}
      {/*      RTL (Arabic)  → from the RIGHT                               */}
      {/*  - Width is intentionally narrower than full screen (82% / max    */}
      {/*    340px) so a sliver of the page peeks through, keeping the     */}
      {/*    drawer feeling like a panel instead of a full takeover.       */}
      {/*  - Uses a soft translucent / glass-like surface (similar in       */}
      {/*    spirit to the language button) without becoming so blurry     */}
      {/*    that text loses crispness.                                    */}
      {/*                                                                   */}
      {/*  IMPORTANT: this section MUST live OUTSIDE <header>. The header  */}
      {/*  uses `backdrop-filter` (`backdrop-blur`), which per CSS spec    */}
      {/*  becomes the containing block for any `position: fixed`         */}
      {/*  descendants — that would clamp the drawer's `inset-y-0` to the */}
      {/*  68px header height instead of the viewport, leaving the drawer */}
      {/*  invisible. Keeping the drawer as a sibling of the header makes */}
      {/*  its containing block the viewport, so it fills the full height */}
      {/*  and slides in correctly.                                        */}
      {/* ----------------------------------------------------------------- */}

      {/* Backdrop — separate node so we can fade it independently of the
          drawer's slide animation, and so taps anywhere outside the panel
          dismiss the menu. */}
      <div
        className={`lg:hidden fixed inset-0 z-40 bg-black/40 transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
        data-testid="mobile-menu-backdrop"
      />

      {/* Drawer panel itself */}
      <aside
        id="mobile-drawer"
        className={`lg:hidden fixed inset-y-0 ${
          lang === "ar" ? "right-0" : "left-0"
        } z-50 w-[82%] max-w-[340px] flex flex-col transition-transform duration-300 ease-out will-change-transform ${
          open
            ? "translate-x-0"
            : lang === "ar"
            ? "translate-x-full"
            : "-translate-x-full"
        }`}
        style={{
          background:
            "linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,255,255,0.92) 100%)",
          backdropFilter: "saturate(160%) blur(16px)",
          WebkitBackdropFilter: "saturate(160%) blur(16px)",
          borderInlineEnd: "1px solid rgba(15, 13, 24, 0.08)",
          boxShadow: "0 18px 48px -16px rgba(20, 16, 50, 0.25)",
        }}
        aria-hidden={!open}
        aria-label={t.mobileNav}
        data-testid="mobile-drawer"
      >
        {/* Drawer header — brand + close button. Mirrors the public header
            height so the drawer feels anchored to the same baseline. */}
        <div
          className="flex items-center justify-between gap-3 px-4 h-[68px]"
          style={{ borderBottom: "1px solid rgba(15, 13, 24, 0.08)" }}
        >
          <LocalizedLink
            to="/"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 min-w-0"
            aria-label={t.brand}
            data-testid="mobile-drawer-logo"
          >
            <img
              src="/sas-logo.svg"
              alt=""
              aria-hidden
              className="h-8 w-8 shrink-0"
            />
            <span className="text-[13px] font-semibold text-[color:var(--sas-ink)] truncate">
              {t.brand}
            </span>
          </LocalizedLink>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={t.menuClose}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full sas-glass shrink-0 text-[color:var(--sas-ink)]"
            data-testid="mobile-drawer-close"
            tabIndex={open ? 0 : -1}
          >
            <X size={16} aria-hidden />
          </button>
        </div>

        {/* Drawer body — link list + utility row */}
        <div className="flex-1 overflow-y-auto px-4 py-5">
          <nav
            className="flex flex-col gap-1"
            aria-label={t.mobileNav}
          >
            {mobileLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                onClick={() => setOpen(false)}
                tabIndex={open ? 0 : -1}
                className={({ isActive }) =>
                  `block rounded-lg px-3 py-2.5 text-[15px] transition-colors ${
                    isActive
                      ? "bg-[color:var(--sas-accent-soft)]/70 text-[color:var(--sas-ink)] font-semibold"
                      : "text-[color:var(--sas-ink-2)] hover:bg-[color:var(--sas-accent-soft)]/40 hover:text-[color:var(--sas-ink)]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Language row — kept inside the drawer so the top header can
              stay minimal on the smallest phones if a user prefers. */}
          <div
            className="flex items-center justify-between mt-5 pt-4"
            style={{ borderTop: "1px solid rgba(15, 13, 24, 0.08)" }}
            data-testid="drawer-lang-row"
          >
            <span className="text-[12px] text-[color:var(--sas-ink-2)]">
              {lang === "ar" ? "اللغة" : "Language"}
            </span>
            <LanguageSwitcher testId="lang-switcher-drawer" />
          </div>

          {/* Theme toggle — full-width row matched to the language row.
              Shown inside the drawer per the brief: "must appear as an
              option with icon + label, easy to access". */}
          <ThemeToggle
            variant="inline"
            lang={lang}
            testId="theme-toggle-drawer"
          />

          <AuthWidget
            status={status}
            user={user}
            lang={lang}
            labels={t}
            variant="drawer"
            onNavigate={() => setOpen(false)}
          />
        </div>
      </aside>
    </>
  );
}

function AuthWidget({ status, user, lang, labels, variant, onNavigate }) {
  if (status === "loading") {
    // Avoid layout shift / flash while the first /me is in flight.
    return null;
  }

  if (variant === "drawer") {
    // Mobile drawer block: full-width buttons consistent with the rest of the menu
    if (status === "authenticated") {
      return (
        <LocalizedLink
          to="/account"
          onClick={onNavigate}
          className="sas-btn sas-btn-ghost w-full mt-2"
          data-testid="nav-account-drawer"
        >
          <User size={15} aria-hidden />
          <span className="truncate">{user?.name || labels.account}</span>
        </LocalizedLink>
      );
    }
    return (
      <LocalizedLink
        to="/auth/login"
        onClick={onNavigate}
        className="sas-btn sas-btn-primary w-full mt-2"
        data-testid="nav-signin-drawer"
      >
        <LogIn size={15} aria-hidden />
        {labels.signInCta}
      </LocalizedLink>
    );
  }

  // Desktop + compact mobile-header variants share this compact look
  const sizeCls = variant === "mobile" ? "w-10 h-10" : "h-9";

  if (status === "authenticated") {
    const initial = (user?.name || user?.email || "?").slice(0, 1).toUpperCase();
    // Mobile CTA variant: a compact avatar-only chip (no name) sized to match
    // the adjacent 40×40 icons.
    if (variant === "mobile-cta") {
      return (
        <LocalizedLink
          to="/account"
          aria-label={labels.account}
          title={labels.account}
          className="inline-flex items-center justify-center w-10 h-10 rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] transition-colors bg-white"
          data-testid="nav-account-mobile"
        >
          {user?.picture ? (
            <img
              src={user.picture}
              alt=""
              aria-hidden
              className="w-7 h-7 rounded-full object-cover"
            />
          ) : (
            <span
              className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[12px] font-semibold"
              style={{ background: "var(--sas-accent-soft)", color: "var(--sas-accent)" }}
              aria-hidden
            >
              {initial}
            </span>
          )}
        </LocalizedLink>
      );
    }
    const testId =
      variant === "mobile"
        ? "nav-account-mobile"
        : variant === "desktop-cta"
        ? "nav-account"
        : "nav-account";
    return (
      <LocalizedLink
        to="/account"
        aria-label={labels.account}
        title={labels.account}
        className={`inline-flex items-center gap-2 rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] transition-colors bg-white pe-3 ps-1 ${sizeCls}`}
        data-testid={testId}
      >
        {user?.picture ? (
          <img
            src={user.picture}
            alt=""
            aria-hidden
            className="w-7 h-7 rounded-full object-cover"
          />
        ) : (
          <span
            className="w-7 h-7 rounded-full inline-flex items-center justify-center text-[12px] font-semibold"
            style={{ background: "var(--sas-accent-soft)", color: "var(--sas-accent)" }}
            aria-hidden
          >
            {initial}
          </span>
        )}
        {variant !== "mobile" && (
          <span className="text-[13px] font-medium text-[color:var(--sas-ink)] max-w-[10ch] truncate">
            {user?.name || labels.account}
          </span>
        )}
      </LocalizedLink>
    );
  }

  // Unauthenticated — desktop primary CTA
  if (variant === "desktop-cta") {
    return (
      <LocalizedLink
        to="/auth/login"
        className="sas-btn sas-btn-primary whitespace-nowrap"
        data-testid="nav-signin"
      >
        <LogIn size={15} aria-hidden />
        {labels.signInCta}
      </LocalizedLink>
    );
  }

  // Unauthenticated — compact mobile primary CTA (shows full "تسجيل الدخول" text)
  if (variant === "mobile-cta") {
    return (
      <LocalizedLink
        to="/auth/login"
        className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-full font-semibold text-[12.5px] whitespace-nowrap transition-colors"
        style={{ background: "var(--sas-accent)", color: "#fff" }}
        data-testid="nav-signin-mobile"
        aria-label={labels.signInCta}
      >
        <LogIn size={14} aria-hidden />
        <span>{labels.signInCta}</span>
      </LocalizedLink>
    );
  }

  // Unauthenticated — compact mobile (icon-only; title gives the full label)
  if (variant === "mobile") {
    return (
      <LocalizedLink
        to="/auth/login"
        aria-label={labels.signInCta}
        title={labels.signInCta}
        className={`inline-flex items-center justify-center rounded-full border border-[color:var(--sas-line)] hover:border-[color:var(--sas-ink)] transition-colors bg-white ${sizeCls} w-10`}
        data-testid="nav-signin-mobile"
      >
        <LogIn size={16} aria-hidden />
      </LocalizedLink>
    );
  }

  // Fallback: text link
  return (
    <LocalizedLink
      to="/auth/login"
      className="text-[13px] font-medium text-[color:var(--sas-ink)] hover:text-[color:var(--sas-accent)] transition-colors px-2 whitespace-nowrap"
      data-testid="nav-signin"
    >
      {labels.signInCta}
    </LocalizedLink>
  );
}
